import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { EmptyState } from 'components/EmptyState';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import api, { WS_BASE, getApiErrorMessage } from 'lib/api';
import { getAccessToken } from 'lib/session';
import { toast } from 'sonner';
import { Send, MessageCircle, ArrowLeft, Flag, Star, Trash2, ShieldCheck, LockKeyhole } from 'lucide-react';

export default function MessagesPage() {
  const { otherId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoriteSearch, setFavoriteSearch] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [sending, setSending] = useState(false);
  const filteredFavorites = favorites.filter(favorite => (favorite.full_name || '').toLocaleLowerCase().includes(favoriteSearch.toLocaleLowerCase()));
  const scrollRef = useRef(null);
  const messageRequest = useRef(0);
  const threadRequest = useRef(0);

  const loadThreads = useCallback(async () => {
    const request = ++threadRequest.current;
    try {
      const { data } = await api.get('/messages/threads');
      if (request === threadRequest.current) setThreads(data);
    } catch (err) { toast.error(getApiErrorMessage(err, t('messages.loadError'))); }
  }, [t]);
  const loadMessages = useCallback(async () => {
    if (!otherId) return;
    const request = ++messageRequest.current;
    try {
      const [conversation, profile, policy] = await Promise.all([
        api.get(`/messages/with/${otherId}`), api.get(`/users/${otherId}`).catch(() => null),
        api.get(`/messages/policy/${otherId}`).catch(() => null),
      ]);
      if (request !== messageRequest.current) return;
      setMessages(conversation.data);
      setOtherUser(profile?.data || null);
      setContactUnlocked(policy?.data?.contact_unlocked === true);
      scrollRef.current?.scrollTo?.(0, scrollRef.current.scrollHeight);
    } catch (err) { toast.error(getApiErrorMessage(err, t('messages.loadError'))); }
  }, [otherId, t]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    setMessages([]);
    setOtherUser(null);
    setContactUnlocked(false);
    setContent('');
    loadMessages();
    return () => { messageRequest.current += 1; };
  }, [loadMessages]);
  useEffect(() => {
    if (!otherId) return;
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [otherId, loadMessages]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let ws;
    try {
      ws = new WebSocket(`${WS_BASE}?token=${encodeURIComponent(token)}`);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            loadThreads();
            if (otherId && (data.message.sender_id === otherId || data.message.recipient_id === otherId)) {
              loadMessages();
            }
          }
          if (['message_deleted', 'conversation_deleted'].includes(data.type)) {
            loadThreads();
            if (otherId === data.other_id) loadMessages();
          }
        } catch { /* ignore malformed frame */ }
      };
    } catch { /* WebSocket unavailable, polling remains as fallback */ }
    return () => ws?.close();
  }, [otherId, loadThreads, loadMessages]);

  const send = async (e) => {
    e.preventDefault();
    if (!content.trim() || !otherId || sending || deleting) return;
    setSending(true);
    try {
      await api.post('/messages', { recipient_id: otherId, content });
      setContent('');
      loadMessages();
      loadThreads();
    } catch (err) { toast.error(getApiErrorMessage(err, t('messages.notSent'))); }
    finally { setSending(false); }
  };

  const openFavorites = async () => {
    setFavoritesOpen(true);
    setFavoriteSearch('');
    setFavoritesLoading(true);
    try {
      const { data } = await api.get('/users/me/favorites');
      setFavorites(data.filter(favorite => favorite.id !== user.id));
    } catch (err) {
      setFavorites([]);
      toast.error(getApiErrorMessage(err, t('messages.favoritesError')));
    } finally { setFavoritesLoading(false); }
  };

  const removeMessage = async (message) => {
    if (deleting || !window.confirm(t('messages.deleteMessageConfirm'))) return;
    setDeleting(true);
    messageRequest.current += 1;
    try {
      await api.delete(`/messages/${message.id}`);
      setMessages(current => current.filter(item => item.id !== message.id));
      await loadThreads();
      await loadMessages();
      toast.success(t('messages.messageDeleted'));
    } catch (err) { toast.error(getApiErrorMessage(err, t('messages.deleteError'))); }
    finally { setDeleting(false); }
  };

  const removeConversation = async () => {
    if (deleting || !otherId || !window.confirm(t('messages.deleteConversationConfirm'))) return;
    setDeleting(true);
    messageRequest.current += 1;
    try {
      await api.delete(`/messages/with/${otherId}`);
      messageRequest.current += 1;
      setMessages([]);
      navigate('/messages');
      await loadThreads();
      toast.success(t('messages.conversationDeleted'));
    } catch (err) { toast.error(getApiErrorMessage(err, t('messages.deleteError'))); }
    finally { setDeleting(false); }
  };

  const reportMessage = async (message) => {
    if (!window.confirm(t('messages.reportConfirm'))) return;
    try {
      await api.post(`/users/${message.sender_id}/report`, {
        reason: 'harassment',
        description: `Message signalé : "${message.content}"`,
      });
      toast.success(t('messages.reported'));
    } catch (err) { toast.error(getApiErrorMessage(err, t('messages.noReport'))); }
  };

  return (
    <DashboardShell>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold">{t('nav.messages')}</h1>
        <Button variant="outline" onClick={openFavorites} data-testid="message-favorite-button"><Star size={16} className="mr-2" />{t('messages.writeFavorite')}</Button>
      </div>
      <Dialog open={favoritesOpen} onOpenChange={setFavoritesOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('messages.chooseFavorite')}</DialogTitle></DialogHeader>
          <Input value={favoriteSearch} onChange={event => setFavoriteSearch(event.target.value)} placeholder={t('messages.searchFavorite')} aria-label={t('messages.searchFavorite')} />
          <div className="max-h-80 overflow-y-auto space-y-2">
            {favoritesLoading ? <p className="text-sm text-muted-foreground">{t('messages.loadingFavorites')}</p> : filteredFavorites.map(favorite => (
              <button key={favorite.id} type="button" className="flex items-center gap-3 w-full rounded-lg border p-3 text-left hover:bg-secondary" onClick={() => { setFavoritesOpen(false); navigate(`/messages/${favorite.id}`); }} data-testid={`message-favorite-${favorite.id}`}>
                <Avatar><AvatarFallback>{(favorite.full_name || 'U').slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <span className="font-medium">{favorite.full_name || t('messages.user')}</span>
                <MessageCircle size={16} className="ml-auto text-primary" />
              </button>
            ))}
            {!favoritesLoading && favorites.length === 0 && <p className="py-4 text-sm text-muted-foreground">{t('messages.noFavorites')}</p>}
            {!favoritesLoading && favorites.length > 0 && filteredFavorites.length === 0 && <p className="py-4 text-sm text-muted-foreground">{t('messages.noFavoriteMatch')}</p>}
          </div>
        </DialogContent>
      </Dialog>
      <div className="grid lg:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-220px)]">
        <Card className={`${otherId ? 'hidden lg:block' : ''}`}>
          <CardContent className="p-0 h-full overflow-y-auto">
            {threads.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">{t('messages.noConversation')}</div>
            ) : (
              threads.map(thread => (
                <button
                  key={thread.thread_id}
                  onClick={() => navigate(`/messages/${thread.other_user_id}`)}
                  className={`flex items-center gap-3 w-full p-3 border-b text-left hover:bg-secondary ${otherId === thread.other_user_id ? 'bg-secondary' : ''}`}
                  data-testid={`thread-${thread.other_user_id}`}
                >
                  <Avatar><AvatarFallback className="bg-secondary text-xs">{(thread.other_user_name || 'U').split(' ').map(n => n[0]).slice(0,2).join('')}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{thread.other_user_name || t('messages.user')}</div>
                    <div className="text-xs text-muted-foreground truncate">{thread.last_message?.content}</div>
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className={`flex flex-col ${!otherId ? 'hidden lg:flex' : ''}`}>
          {otherId ? (
            <>
              <div className="flex items-center gap-2 p-3 border-b">
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => navigate('/messages')}>
                  <ArrowLeft size={18} />
                </Button>
                <Avatar><AvatarFallback className="bg-secondary text-xs">{(otherUser?.full_name || 'U').split(' ').map(n => n[0]).slice(0,2).join('')}</AvatarFallback></Avatar>
                <div>
                  <div className="font-semibold text-sm">{otherUser?.full_name || t('messages.user')}</div>
                </div>
                <Button variant="ghost" size="icon" className="ml-auto text-destructive" disabled={deleting || sending} onClick={removeConversation} title={t('messages.deleteConversation')} aria-label={t('messages.deleteConversation')} data-testid="delete-conversation-button"><Trash2 size={18} /></Button>
              </div>
              <div className={`flex items-start gap-2 border-b px-4 py-3 text-xs ${contactUnlocked ? 'bg-emerald-50 text-emerald-800' : 'bg-secondary/60 text-muted-foreground'}`} data-testid="message-contact-policy" role="status">
                {contactUnlocked ? <ShieldCheck size={16} className="mt-0.5 shrink-0" /> : <LockKeyhole size={16} className="mt-0.5 shrink-0" />}
                <p>{contactUnlocked ? t('messages.contactsUnlocked') : t('messages.contactsLocked')}</p>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-list">
                {messages.map(m => {
                  const me = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex items-end gap-1 ${me ? 'justify-end' : 'justify-start'}`}>
                      {!me && (
                        <button type="button" onClick={() => reportMessage(m)} className="text-muted-foreground hover:text-destructive" title={t('messages.report')}>
                          <Flag size={12} />
                        </button>
                      )}
                      <div className={`max-w-[75%] px-3 py-2 text-sm ${me ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md' : 'bg-secondary text-foreground rounded-2xl rounded-bl-md'}`}>
                        {m.content}
                        <div className={`text-[10px] mt-1 ${me ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                          {new Date(m.created_at).toLocaleTimeString(language === 'en' ? 'en-GB' : 'fr-FR', {hour: '2-digit', minute: '2-digit'})}
                        </div>
                      </div>
                      <button type="button" disabled={deleting} onClick={() => removeMessage(m)} className="p-1 text-muted-foreground hover:text-destructive disabled:opacity-50" title={t('messages.deleteMessage')} aria-label={t('messages.deleteMessage')} data-testid={`delete-message-${m.id}`}><Trash2 size={14} /></button>
                    </div>
                  );
                })}
                {messages.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">{t('messages.start')}</div>}
              </div>
              <form onSubmit={send} className="p-3 border-t flex gap-2">
                <Input value={content} maxLength={4000} onChange={e => setContent(e.target.value)} placeholder={t('messages.write')} data-testid="chat-input" />
                <Button type="submit" disabled={!content.trim() || sending || deleting} data-testid="chat-send-button"><Send size={16} /></Button>
              </form>
            </>
          ) : (
            <EmptyState icon={MessageCircle} title={t('messages.select')} description={t('messages.contact')} />
          )}
        </Card>
      </div>
    </DashboardShell>
  );
}
